/**
 * The two lead forms of the approved mock-up: the full contact form and the
 * home page's "we will call you back" quick form.
 *
 * Both render real labels (the mock-up used placeholders only, which leaves a
 * field unlabelled once the user starts typing), validate before submitting, and
 * only show the thank-you state when the gateway confirmed the lead.
 */
import { clear, el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { ServiceCategory } from "../data/types.js";
import { submitForm, type LeadContext } from "../data/leads.js";

interface FieldSpec {
	name: string;
	label: string;
	type: "text" | "email" | "tel" | "textarea" | "select";
	required?: boolean;
	autocomplete?: string;
	options?: { label: string; value: string }[];
	placeholder?: string;
}

interface Field {
	wrapper: HTMLElement;
	control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
	spec: FieldSpec;
	error: HTMLElement;
}

let fieldSeq = 0;

function field(spec: FieldSpec): Field {
	const id = `mb-field-${spec.name}-${(fieldSeq += 1)}`;
	const errorId = `${id}-error`;
	const error = el("p", { class: "mb-field__error", id: errorId, role: "alert", hidden: true });

	// The mock-up puts the field's wording in `placeholder`; we keep the visible
	// <label> (a placeholder disappears the moment the user types, which is the
	// accessibility problem the label solves) and mirror the same string into the
	// placeholder so the rendered wording matches the design either way.
	// `select` is excluded: there the placeholder string is the empty option.
	const placeholder = spec.placeholder ?? (spec.type === "select" ? undefined : spec.label);

	const shared = {
		id,
		name: spec.name,
		class: spec.type === "textarea" ? "mb-field__input mb-field__input--area" : "mb-field__input",
		"aria-describedby": errorId,
		...(spec.required ? { required: true } : {}),
		...(spec.autocomplete ? { autocomplete: spec.autocomplete } : {}),
		...(placeholder ? { placeholder } : {}),
	};

	let control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
	if (spec.type === "textarea") {
		control = el("textarea", { ...shared, rows: "5" });
	} else if (spec.type === "select") {
		const select = el("select", shared);
		select.appendChild(el("option", { value: "" }, spec.placeholder ?? spec.label));
		for (const option of spec.options ?? []) {
			select.appendChild(el("option", { value: option.value }, option.label));
		}
		control = select;
	} else {
		control = el("input", { ...shared, type: spec.type });
	}

	const wrapper = el(
		"div",
		{ class: "mb-field" },
		el("label", { class: "mb-field__label", for: id }, spec.label),
		control,
		error,
	);
	return { wrapper, control, spec, error };
}

function validate(ctx: AppContext, fields: Field[]): boolean {
	let firstInvalid: Field | null = null;
	for (const f of fields) {
		const value = f.control.value.trim();
		let message = "";
		if (f.spec.required && value === "") message = ctx.t("form.required");
		else if (f.spec.type === "email" && value !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
			message = ctx.t("form.invalidEmail");
		else if (f.spec.type === "tel" && value !== "" && !/^[+\d][\d\s()-]{6,}$/.test(value))
			message = ctx.t("form.invalidPhone");

		f.error.hidden = message === "";
		f.error.textContent = message;
		f.control.setAttribute("aria-invalid", String(message !== ""));
		if (message !== "" && !firstInvalid) firstInvalid = f;
	}
	firstInvalid?.control.focus();
	return firstInvalid === null;
}

function values(fields: Field[]): Record<string, string> {
	const out: Record<string, string> = {};
	for (const f of fields) out[f.spec.name] = f.control.value.trim();
	return out;
}

function successPanel(
	title: string,
	description: string,
	againLabel: string,
	onAgain: () => void,
): HTMLElement {
	return el(
		"div",
		{ class: "mb-formdone", role: "status" },
		el("span", { class: "mb-formdone__tick", "aria-hidden": "true" }, "✓"),
		el("h3", { class: "mb-formdone__title" }, title),
		el("p", { class: "mb-formdone__desc" }, description),
		el("button", { class: "mb-cta mb-cta--solid", type: "button", onClick: onAgain }, againLabel),
	);
}

// --- contact form ------------------------------------------------------------

export function contactForm(ctx: AppContext, categories: ServiceCategory[]): HTMLElement {
	const copy = ctx.copy();
	const host = el("div", { class: "mb-formcard" });
	const leadCtx: LeadContext = { client: ctx.client, site: ctx.site, locale: ctx.locale() };

	function render(): void {
		clear(host);

		const fields: Field[] = [
			field({
				name: "name",
				label: copy.contact.name,
				type: "text",
				required: true,
				autocomplete: "name",
			}),
			field({
				name: "company",
				label: copy.contact.company,
				type: "text",
				autocomplete: "organization",
			}),
			field({
				name: "email",
				label: copy.contact.email,
				type: "email",
				required: true,
				autocomplete: "email",
			}),
			field({
				name: "category",
				label: copy.contact.categoryPlaceholder,
				type: "select",
				options: categories.map((c) => ({ label: c.title, value: c.slug })),
			}),
			field({ name: "message", label: copy.contact.message, type: "textarea", required: true }),
		];

		const submit = el(
			"button",
			{ class: "mb-cta mb-cta--accent mb-form__submit", type: "submit" },
			copy.contact.submit,
		);
		const formError = el("p", { class: "mb-field__error", role: "alert", hidden: true });

		const form = el(
			"form",
			{
				class: "mb-form",
				novalidate: true,
				onSubmit: (event: Event) => {
					event.preventDefault();
					void send();
				},
			},
			el("h2", { class: "mb-formcard__title" }, copy.contact.formTitle),
			...fields.map((f) => f.wrapper),
			submit,
			formError,
			el("p", { class: "mb-form__note" }, copy.contact.privacyNote),
		);

		async function send(): Promise<void> {
			formError.hidden = true;
			if (!validate(ctx, fields)) return;
			submit.setAttribute("disabled", "");
			submit.textContent = ctx.t("form.submitting");

			const outcome = await submitForm(leadCtx, "contact", "contact_form", values(fields));

			submit.removeAttribute("disabled");
			submit.textContent = copy.contact.submit;

			if (outcome.ok) {
				ctx.track({ type: "lead_submit", form: "contact" });
				clear(host);
				host.appendChild(
					successPanel(
						copy.contact.sentTitle,
						outcome.message ?? copy.contact.sentDesc,
						copy.contact.sendAnother,
						render,
					),
				);
				return;
			}
			formError.hidden = false;
			formError.textContent = outcome.unavailable
				? ctx.t("form.genericError")
				: (outcome.message ?? ctx.t("form.genericError"));
		}

		host.appendChild(form);
	}

	render();
	return host;
}

// --- quick callback form -----------------------------------------------------

export function callbackForm(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	const host = el("div", { class: "mb-formcard mb-formcard--quick" });
	const leadCtx: LeadContext = { client: ctx.client, site: ctx.site, locale: ctx.locale() };

	function render(): void {
		clear(host);
		let slot = 3; // "Dowolna" / "Any time" — the least demanding default

		const fields: Field[] = [
			field({
				name: "name",
				label: copy.quickForm.name,
				type: "text",
				required: true,
				autocomplete: "name",
			}),
			field({
				name: "phone",
				label: copy.quickForm.phone,
				type: "tel",
				required: true,
				autocomplete: "tel",
			}),
		];

		const slotButtons = copy.quickForm.slots.map((label, index) =>
			el(
				"button",
				{
					class: `mb-slot${index === slot ? " is-active" : ""}`,
					type: "button",
					"aria-pressed": String(index === slot),
					onClick: () => {
						slot = index;
						slotButtons.forEach((button, i) => {
							button.classList.toggle("is-active", i === index);
							button.setAttribute("aria-pressed", String(i === index));
						});
					},
				},
				label,
			),
		);

		const slotGroup = el(
			"fieldset",
			{ class: "mb-slots" },
			el("legend", { class: "mb-field__label" }, copy.quickForm.slotLabel),
			el("div", { class: "mb-slots__row" }, ...slotButtons),
		);

		const submit = el(
			"button",
			{ class: "mb-cta mb-cta--accent mb-form__submit", type: "submit" },
			copy.quickForm.submit,
		);
		const formError = el("p", { class: "mb-field__error", role: "alert", hidden: true });

		const form = el(
			"form",
			{
				class: "mb-form",
				novalidate: true,
				onSubmit: (event: Event) => {
					event.preventDefault();
					void send();
				},
			},
			el("h2", { class: "mb-formcard__title" }, copy.quickForm.title),
			el("p", { class: "mb-formcard__desc" }, copy.quickForm.desc),
			...fields.map((f) => f.wrapper),
			slotGroup,
			submit,
			formError,
			el("a", { class: "mb-form__altlink", href: ctx.href("contact") }, copy.quickForm.mailInstead),
		);

		async function send(): Promise<void> {
			formError.hidden = true;
			if (!validate(ctx, fields)) return;
			submit.setAttribute("disabled", "");
			submit.textContent = ctx.t("form.submitting");

			const outcome = await submitForm(leadCtx, "callback", "quick_sales_contact", {
				...values(fields),
				slot: copy.quickForm.slots[slot] ?? "",
			});

			submit.removeAttribute("disabled");
			submit.textContent = copy.quickForm.submit;

			if (outcome.ok) {
				ctx.track({ type: "lead_submit", form: "callback" });
				clear(host);
				host.appendChild(
					successPanel(
						copy.quickForm.sentTitle,
						outcome.message ?? copy.quickForm.sentDesc,
						copy.quickForm.sendAnother,
						render,
					),
				);
				return;
			}
			formError.hidden = false;
			formError.textContent = outcome.unavailable
				? ctx.t("form.genericError")
				: (outcome.message ?? ctx.t("form.genericError"));
		}

		host.appendChild(form);
	}

	render();
	return host;
}
